import type { QueryClient } from '@tanstack/react-query';
import {
  MessageDocument,
  MessageStatus,
  type Message,
  type TCSearcherTimelineItemDocument as SearcherTimelineItemDocument,
} from 'src/api/generated';
import { getMessageClientMessageId } from 'src/modules/shared/messaging/utils/client-message-id.util';
import { type WhatsAppMessageContext } from 'src/modules/shared/messaging/types/whatsapp-message-context.type';
import { shouldAddTimelineItemToQuery } from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import {
  updateCache,
  type InfiniteQueryData,
} from 'src/modules/shared/utils/cache/update.util';
import { canApplyMessageMutationSnapshot } from 'src/modules/shared/realtime/communications/utils/cache/event-order.util';

const TIMELINE_QUERY_KEY = ['crm/timeline-items/search'];

/**
 * Preserves server identities, falling back to the send's optimistic scope.
 * @param message - Local or canonical message snapshot.
 * @param params - Captured send context.
 * @param optimisticMessage - Original local identities when available.
 * @returns A timeline document scoped to the captured conversation.
 */
export const toWhatsAppTimelineItem = (
  message: Message,
  params: WhatsAppMessageContext['params'],
  optimisticMessage?: Message
): SearcherTimelineItemDocument => {
  const optimisticData = params.optimisticData;

  return {
    ...message,
    funnelConversationId:
      message.funnelConversationId?.toString() ??
      optimisticMessage?.funnelConversationId?.toString() ??
      optimisticData?.funnelConversationId?.toString(),
    sessionId:
      message.sessionId?.toString() ??
      optimisticMessage?.sessionId?.toString() ??
      optimisticData?.sessionId?.toString(),
    contactId:
      message.contactId?.toString() ??
      optimisticMessage?.contactId?.toString() ??
      optimisticData?.contactId?.toString(),
    funnelId: optimisticData?.funnelId?.toString(),
    modelType: MessageDocument.modelType.MESSAGE,
  };
};

/** Match only this send's placeholder, never a persisted or unrelated row. */
const isOptimisticMessageForSend = (
  item: SearcherTimelineItemDocument,
  clientMessageId: string | undefined
): boolean => {
  if (
    !clientMessageId ||
    item.modelType !== MessageDocument.modelType.MESSAGE ||
    !item.id.startsWith('temp-')
  ) {
    return false;
  }

  return (
    item.id === clientMessageId ||
    getMessageClientMessageId(item) === clientMessageId
  );
};

/** Retire the placeholder across pages without applying a stale server snapshot. */
const removeOptimisticMessageFromTimelines = (
  queryClient: QueryClient,
  clientMessageId: string | undefined
): void => {
  if (!clientMessageId) return;

  queryClient.setQueriesData<InfiniteQueryData<SearcherTimelineItemDocument>>(
    { queryKey: TIMELINE_QUERY_KEY },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter(
            (item) => !isOptimisticMessageForSend(item, clientMessageId)
          ),
        })),
      }
  );
};

/**
 * Reconciles a send's optimistic state or accepted server response with timelines.
 * A server snapshot refused by the ordering check still retires its own
 * optimistic row; newer cached snapshots and realtime deletions retain authority.
 * @param message - Optimistic pending/failed message or server send response.
 * @param context - Query client, send scope and retained mutation ordering.
 * @returns Nothing; updates existing matching timeline caches.
 */
export const createWhatsAppMessageCacheUpdate = (
  message: Message,
  context: WhatsAppMessageContext
): void => {
  const isOptimisticMessage = message.id.startsWith('temp-');
  const clientMessageId =
    getMessageClientMessageId(message) ?? context.optimisticMessage?.id;
  const canApplySnapshot =
    isOptimisticMessage ||
    canApplyMessageMutationSnapshot(
      context.queryClient,
      message,
      context.messageOrdering
    );

  if (!canApplySnapshot) {
    removeOptimisticMessageFromTimelines(context.queryClient, clientMessageId);
    // Uncertain ordering needs HTTP recovery, not a guess from this response.
    if (context.messageOrdering?.needsRecovery()) {
      void context.queryClient.invalidateQueries({
        queryKey: TIMELINE_QUERY_KEY,
        refetchType: 'active',
      });
    }
    return;
  }

  // Failed optimistic rows may update an existing placeholder, never insert one.
  const canInsertMessage =
    !isOptimisticMessage || message.status === MessageStatus.PENDING;

  updateCache({
    queryClient: context.queryClient,
    queryKey: TIMELINE_QUERY_KEY,
    updatedData: toWhatsAppTimelineItem(
      message,
      context.params,
      context.optimisticMessage
    ),
    shouldAddItem: (matchedKey, newItem) =>
      canInsertMessage && shouldAddTimelineItemToQuery(matchedKey, newItem),
    shouldRemoveItem: (_, cachedItem) =>
      !isOptimisticMessage &&
      isOptimisticMessageForSend(cachedItem, clientMessageId),
  });
};
